/**
 * Which ingestion providers have a rules page, and where it lives.
 *
 * One page per provider, sectioned by rule tier — see
 * `docs/platform/learned-match-rules-design.md` § "5c — Surfacing: one hub, honest
 * labels". The page itself is a single shared component
 * ({@link IngestionRulesPage}); this table is the only per-provider part, so a
 * module never hand-rolls its own copy.
 *
 * A provider earns an entry when at least ONE tier applies to it: a `learnable`
 * matchable role (Tier 1 — Identity), or a registered Tier-2 / Tier-3 section. A
 * provider with no applicable tier deliberately gets NO page — a page whose every
 * section is omitted is the empty-heading problem 5c exists to fix, not a fix for
 * it. Both halves of that rule are pinned against the live backend registry by
 * `shared/__tests__/test_ingestion_rules_page_coverage.py`, so adding a learnable
 * role to a page-less provider fails until it gets an entry here.
 */

import { ingestionImportSurfaceFor } from './importSurfaces';

export interface IngestionRulesPageEntry {
  /** The backend provider key, e.g. `accounting.bill`. */
  targetEntityType: string;
  /** Route path for this provider's rules page. */
  path: string;
  /**
   * The provider's own `label_i18n_key`, mirrored from its backend registration —
   * the page title and the "back to import" breadcrumb both read it. Kept in step
   * with the backend by the coverage test.
   */
  labelI18nKey: string;
}

export const INGESTION_RULES_PAGES: readonly IngestionRulesPageEntry[] = [
  {
    targetEntityType: 'accounting.bill',
    path: '/accounting/import-bills/rules',
    labelI18nKey: 'accounting:ingestion.bill.label',
  },
  {
    targetEntityType: 'procurement.purchase_order',
    path: '/procurement/purchase-orders/import-pro-formas/rules',
    labelI18nKey: 'procurement:ingestion.purchaseOrder.label',
  },
  {
    targetEntityType: 'sales.inquiry',
    path: '/sales/inquiries/import/rules', // frontend-route-link-ok: App.tsx maps INGESTION_RULES_PAGES into <Route path={entry.path}> — this entry IS the route declaration
    labelI18nKey: 'sales:ingestion.inquiry.label',
  },
  {
    targetEntityType: 'contracts.contract',
    path: '/contracts/import/rules', // frontend-route-link-ok: App.tsx maps INGESTION_RULES_PAGES into <Route path={entry.path}> — this entry IS the route declaration
    labelI18nKey: 'contracts:contractImport.label',
  },
  {
    targetEntityType: 'master_data.supplier_price_list',
    path: '/procurement/vendors/import-catalogue/rules', // frontend-route-link-ok: App.tsx maps INGESTION_RULES_PAGES into <Route path={entry.path}> — this entry IS the route declaration
    labelI18nKey: 'products:priceListImport.label',
  },
];

export function ingestionRulesPathFor(targetEntityType: string): string | undefined {
  return INGESTION_RULES_PAGES.find((e) => e.targetEntityType === targetEntityType)?.path;
}

/**
 * Where the rules page's "back to import" breadcrumb goes. Read from
 * {@link INGESTION_IMPORT_SURFACES}, the single place a provider's import route is
 * written down — the mismatch banner's destination link reads the same table.
 */
export function ingestionImportSurfacePathFor(targetEntityType: string): string {
  const path = ingestionImportSurfaceFor(targetEntityType);
  if (path === undefined) {
    throw new Error(
      `No import surface registered for '${targetEntityType}' — add it to INGESTION_IMPORT_SURFACES.`,
    );
  }
  return path;
}
